import { useRef, useState } from 'react';

import { Box, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Combobox, type ComboboxChangeEventDetails } from '@opendoor/atoms/combobox';
import { Form } from '@opendoor/atoms/form';
import { Loader } from '@opendoor/atoms/loader';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { useForm } from '@tanstack/react-form';
import { USER_EDIT_REASONS } from 'helpers/useRemoteAutocomplete';

import { type ListingSuggestion } from './listingSearch';
import { useListingSuggestions } from './useListingSuggestions';

/**
 * Hero inventory lookup, mirroring the legacy `ListingTypeAheadInput` flow: the
 * agent picks an offerable listing from suggestions, and submit hands the
 * selection to the caller. Free text is not submittable — unmatched input gets
 * the legacy "Please enter a valid address" on blur, and the CTA stays disabled
 * until a listing is picked.
 */
const listingLabel = (item: ListingSuggestion) => item.readable_address;

const isSameListing = (a: ListingSuggestion, b: ListingSuggestion) => a.slug === b.slug;

const PropertySearchForm = ({ onSubmit }: { onSubmit: (listing: ListingSuggestion) => void }) => {
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [showError, setShowError] = useState(false);
  const { suggestions, isLoading, hasError: lookupFailed, onQueryChange } = useListingSuggestions();

  const inputRef = useRef<HTMLInputElement>(null);
  const form = useForm({
    defaultValues: { listing: null as ListingSuggestion | null },
    onSubmit: ({ value }) => {
      // The button stays enabled (and brand blue); a submit without a picked
      // listing surfaces the validation error and returns focus to the field.
      if (!value.listing) {
        setShowError(true);
        inputRef.current?.focus();
        return;
      }
      onSubmit(value.listing);
    },
  });

  const handleInputValueChange = (value: string, details: ComboboxChangeEventDetails) => {
    // Base UI rewrites the input when the popup closes; only a real edit
    // (typing, or Escape blanking the field) should reset the query,
    // selection, and error. Same reason set as every other cosmos typeahead.
    if (!USER_EDIT_REASONS.includes(details.reason)) {
      return;
    }
    setQuery(value);
    setShowError(false);
    onQueryChange(value);
    if (form.state.values.listing) {
      form.setFieldValue('listing', null);
    }
  };

  const handleListingSelect = (item: ListingSuggestion | null) => {
    if (item) {
      form.setFieldValue('listing', item);
      setQuery(item.readable_address);
      setShowError(false);
    }
  };

  const handleInputFocus = () => setIsFocused(true);

  const handleInputBlur = () => {
    setIsFocused(false);
    // The legacy invalidUserInputMessage: text that never became a
    // selection is not an address we can offer on.
    setShowError(query.trim() !== '' && !form.state.values.listing);
  };

  return (
    <Form.Root form={form} className="w-full max-w-[562px]">
      <form.Subscribe selector={(state) => state.values.listing}>
        {(listing) => (
          <Row
            className={cn(
              'items-center gap-1 rounded-[20px] bg-od-bg-primary/60 py-2 pr-2 pl-5 backdrop-blur-[11.4px]',
              // A single hairline plus the soft drop shadow; layering Figma's
              // inner-relief insets on top reads as a doubled border.
              'shadow-[0px_0px_0px_1px_rgba(0,0,0,0.12),0px_19px_24.1px_-9px_rgba(203,199,198,0.52)]',
              // The field's own chrome is suppressed, so the pill carries the
              // focus indicator; the error state has to beat it or a refocused
              // invalid field reads as merely focused.
              showError
                ? 'ring-2 ring-od-border-error focus-within:ring-od-border-error'
                : 'focus-within:ring-2 focus-within:ring-od-border-focused-accent-brand',
            )}
          >
            <Combobox.Root<ListingSuggestion>
              // sm keeps five suggestions compact enough to fit below the pill;
              // taller lists flip above it and cover the heading.
              size="sm"
              items={suggestions}
              // Already filtered server-side; filtering again would drop matches
              // whose address does not literally contain the query.
              filter={null}
              inputValue={query}
              // Opening is gated on having something to show (suggestions come
              // from the network) and closes once a listing is picked.
              open={isFocused && query !== '' && suggestions.length > 0 && !listing}
              onInputValueChange={handleInputValueChange}
              itemToStringLabel={listingLabel}
              isItemEqualToValue={isSameListing}
              onValueChange={handleListingSelect}
            >
              <Combobox.Input
                ref={inputRef}
                placeholder="Type property address"
                aria-label="Property address"
                aria-invalid={showError}
                aria-describedby={
                  showError || lookupFailed ? 'make-offer-address-error' : undefined
                }
                autoComplete="off"
                // Always-rendered slot: ComboboxInput reparents the input when
                // suffix appears, and a mid-typing remount drops focus and
                // swallows keystrokes the moment the first lookup starts.
                suffix={
                  <Box className="w-6 justify-center">
                    {isLoading ? <Loader size="sm" /> : null}
                  </Box>
                }
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                // !bg: the token background class is opaque to tailwind-merge, so it
                // survives a plain bg-transparent; the data-variant rings are the
                // combobox's own open/filled indicators, which the pill replaces.
                className="h-12 w-full !bg-transparent px-0 text-[16px] tracking-[-0.8px] shadow-none ring-0 not-disabled:data-filled:ring-0 focus:ring-0 data-popup-open:ring-0"
              />
              <Combobox.Popup
                aria-label="Matching Opendoor homes"
                // The component default is a 2px accent ring and a max height
                // that scrolls at five items; a neutral hairline card that fits
                // the full suggestion limit matches the rest of the app.
                className={cn(
                  'max-h-80 !ring-1 !ring-od-border-primary shadow-[0px_12px_32px_-12px_rgba(37,32,29,0.25)]',
                  // Grow in from the pill instead of popping; same Base UI
                  // starting/ending-style idiom the atoms toast uses.
                  'origin-top transition-[transform,opacity] duration-150 ease-out',
                  'data-starting-style:scale-95 data-starting-style:opacity-0',
                  'data-ending-style:scale-95 data-ending-style:opacity-0',
                )}
              >
                <Combobox.List>
                  {(item: ListingSuggestion) => (
                    <Combobox.Item key={item.slug} value={item}>
                      {item.readable_address}
                    </Combobox.Item>
                  )}
                </Combobox.List>
              </Combobox.Popup>
            </Combobox.Root>
            <Button
              type="submit"
              variant="primary"
              className="h-12 shrink-0 rounded-[12px] px-6 text-[16px] tracking-[-0.8px]"
              aria-label="Make offer"
              analyticsName="cosmos-make-offer-landing-submit"
            >
              Make offer
            </Button>
          </Row>
        )}
      </form.Subscribe>
      {/* An outage is not the agent's fault; never dress it up as invalid
          input. The lookup-failed copy wins over the validation copy. */}
      {lookupFailed || showError ? (
        <Text
          id="make-offer-address-error"
          role="alert"
          className="pt-2 text-body-sm text-od-content-error"
        >
          {lookupFailed
            ? 'We can’t look up addresses right now. Please try again in a moment.'
            : 'Please enter a valid address'}
        </Text>
      ) : null}
    </Form.Root>
  );
};

export default PropertySearchForm;
